import { idToStrongId } from "@oysterhr/common"
import { PlatformTypeEnum } from "@oysterhr/schema"
import { generatePath } from "react-router-dom"
import { companyBasePath } from "../../utils/baseRoutes"
import { Platform } from "../types"

export const companyDashboardPath = (companyId?: string) => {
  return companyBasePath({ companyId })
}

// CUSTOMERS
const _resellerCompanyCustomersBasePath = (companyId: string, path?: string) => {
  return companyBasePath({ companyId, path: generatePath("customers/*", { "*": path || "" }) })
}

export const resellerCompanyCustomersPath = (companyId: string) => {
  return _resellerCompanyCustomersBasePath(companyId)
}

export const newResellerCompanyCustomerPath = (companyId: string) => {
  return _resellerCompanyCustomersBasePath(companyId, "new")
}

// PROFILE
export const companyProfilePath = (companyId?: string) => {
  return companyBasePath({ companyId, path: "profile" })
}

// SETTINGS
export const companySettingsPath = (companyId?: string, hash?: string) => {
  return companyBasePath({ companyId, path: "settings", hash })
}

export const companyPointsOfContactPath = (companyId: string) => {
  return companyBasePath({ companyId, path: "settings", hash: "points-of-contact" })
}

export const companyFinanceSettingsPath = (companyId: string) => {
  return companyBasePath({ companyId, path: "settings", hash: "finance" })
}

export const companyAppearancePath = (companyId: string) => {
  return companyBasePath({ companyId, path: "appearance" })
}

// DOCUMENTS
export const companyDocumentsPath = (companyId: string) => {
  return companyBasePath({ companyId, path: "documents" })
}

// REPORTS
const _reportsPath = (companyId: string, path?: string) => {
  return companyBasePath({ companyId, path: generatePath("reports/*", { "*": path || "" }) })
}

export const companyReportsPath = (companyId: string) => {
  return _reportsPath(companyId)
}

export const companyDownloadPayrollReportPath = (companyId: string) => {
  return _reportsPath(companyId, "download_payroll_report")
}

// INTERACTIVE REPORTS
const _interactiveReportsPath = (companyId: string, path?: string) => {
  return companyBasePath({
    companyId,
    path: generatePath("interactive-reports/*", { "*": path || "" }),
  })
}

export const companyInteractiveReportsPath = (companyId: string) => {
  return _interactiveReportsPath(companyId)
}

export const companyInteractiveReportsExplorerReportPath = (
  companyId: string,
  lookerId: number,
) => {
  return _interactiveReportsPath(companyId, `explorer/reports/${lookerId}`)
}

export const companyInteractiveReportsExplorerDashboardPath = (
  companyId: string,
  lookerId: number,
) => {
  return _interactiveReportsPath(companyId, `explorer/dashboards/${lookerId}`)
}

// INTEGRATION
const _integrationsPath = ({
  companyId,
  platformType,
  platform,
  path,
  hash,
}: {
  companyId?: string
  platformType?: string
  platform?: string
  path?: string
  hash?: string
}) => {
  const integrationPlatformPath = generatePath("integrations/:platformType/:platform/*", {
    platformType: platformType?.toLowerCase() || "",
    platform: platform || "",
    "*": path || "",
  })

  return companyBasePath({ companyId, path: integrationPlatformPath, hash })
}

export const companyIntegrationsPath = ({
  companyId,
  hash,
}: { companyId?: string; hash?: string } = {}) => {
  return _integrationsPath({ companyId, hash })
}

export const companyIntegrationPath = (
  companyId: string,
  platformType: PlatformTypeEnum,
  platform: Platform,
) => {
  return _integrationsPath({ companyId, platformType, platform })
}

export const companyIntegrationPlatformConnectSuccessPath = (
  companyId: string,
  platformType: PlatformTypeEnum,
  platform: Platform,
) => {
  return _integrationsPath({ companyId, platformType, platform, path: "success" })
}

export const companyIntegrationSetupPath = (
  companyId: string,
  platformType: PlatformTypeEnum,
  platform: Platform,
) => {
  return _integrationsPath({ companyId, platformType, platform, path: "setup" })
}

export const companyIntegrationSettingsPath = ({
  companyId,
  platformType,
  platform,
  hash,
}: {
  companyId?: string
  platformType: PlatformTypeEnum | string
  platform: Platform | string
  hash?: string
}) => {
  return _integrationsPath({ companyId, platformType, platform, path: "settings", hash })
}

export const companyIntegrationIssuePath = (
  companyId: string,
  platformType: string,
  platform: string,
  alertId: string,
) => {
  return _integrationsPath({
    companyId,
    platformType,
    platform,
    path: `alerts/${idToStrongId(alertId)}`,
  })
}

export const companyIntegrationNewHirePath = (companyId: string, hireId: string) => {
  // DISCLAIMER this route includes `integration` (singular) and not `integrations` (plural)
  return companyBasePath({
    companyId,
    path: `integration/${idToStrongId(hireId)}/hires/new`,
  })
}

// DEVELOPER
const _developerPath = (companyId: string, path?: string) => {
  return companyBasePath({ companyId, path: generatePath("developer/*", { "*": path || "" }) })
}

export const companyOauthApplicationsPath = (companyId: string) => {
  return _developerPath(companyId)
}

export const companyOauthApplicationPath = (companyId: string, oauthApplicationId: string) => {
  return _developerPath(companyId, `${idToStrongId(oauthApplicationId)}`)
}

export const companyNewOauthApplicationPath = (companyId: string) => {
  return _developerPath(companyId, "new")
}

// TOOLS
export const companyGlobalSalaryConverterPath = (companyId: string) => {
  return companyBasePath({ companyId, path: "global-salary-converter" })
}

// INVOICING
export const companyInvoicesPath = (companyId: string, hash?: string) => {
  return companyBasePath({ companyId, path: "invoices", hash })
}

const _invoicesBatchesPath = (companyId: string, path?: string) => {
  return companyBasePath({
    companyId,
    path: generatePath("invoices-batches/*", { "*": path || "" }),
  })
}

export const companyInvoicesBatchesPath = (companyId: string) => {
  return _invoicesBatchesPath(companyId)
}

export const companyNewInvoicesBatchPath = (companyId: string) => {
  return _invoicesBatchesPath(companyId, "new")
}

export const companyInvoicesBatchPath = (companyId: string, invoicesBatchId: string) => {
  return _invoicesBatchesPath(companyId, `${idToStrongId(invoicesBatchId)}`)
}

export const companyAddInvoicesToBatchPath = (companyId: string, invoicesBatchId: string) => {
  return _invoicesBatchesPath(companyId, `${idToStrongId(invoicesBatchId)}/add-invoices`)
}
